"use client";

import { useQuery } from "@tanstack/react-query";
import { createContext, type ReactNode, useContext, useMemo } from "react";
import {
  type PublicTicketsNavLink,
  resolvePublicTicketsNavLink,
} from "@/features/tickets/public/public-tickets-nav";
import { QueryKeys } from "@/lib/types";
import { createOpenApiConfiguration } from "@/lib/utils";
import { resolveEffectiveTenantNumber } from "@/lib/utils/env/effective-tenant";
import { TicketsApi } from "@/openapi/generated";

const PublicTicketsNavContext = createContext<
  PublicTicketsNavLink | null | undefined
>(undefined);

export function PublicTicketsNavProvider({
  value,
  children,
}: {
  value: PublicTicketsNavLink | null | undefined;
  children: ReactNode;
}) {
  return (
    <PublicTicketsNavContext.Provider value={value}>
      {children}
    </PublicTicketsNavContext.Provider>
  );
}

export function usePublicTicketsNavLink(): PublicTicketsNavLink | null {
  const provided = useContext(PublicTicketsNavContext);
  const { data } = useQuery({
    queryKey: [QueryKeys.TicketPublicEvent, "nav"],
    enabled: provided === undefined,
    queryFn: async () => {
      try {
        const api = new TicketsApi(createOpenApiConfiguration());
        const result = await api.apiTicketsEventsGet({
          xTenantID: resolveEffectiveTenantNumber(),
        });
        if (!result.isSuccess || result.data == null) return [];
        return result.data;
      } catch {
        return [];
      }
    },
    staleTime: 60_000,
  });

  return useMemo(() => {
    if (provided !== undefined) return provided;
    return resolvePublicTicketsNavLink(data);
  }, [provided, data]);
}
